import { forwardRef, useImperativeHandle } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ListTodo, ArrowRight, ClipboardCheck } from "lucide-react";
import type { Recommendation } from "@/types/portfolio";

interface ActionPlanPanelProps {
  actionPlan: Recommendation[];
  onSelectCategory?: (categoryKey: string) => void;
  planningProgress?: { completed: number; total: number };
}

export interface ActionPlanPanelRef {
  expand: () => void;
  toggle: () => void;
}

function priorityVariant(p: number): "destructive" | "secondary" | "outline" {
  if (p <= 2) return "destructive";
  if (p === 3) return "secondary";
  return "outline";
}

function priorityLabel(p: number): string {
  if (p <= 2) return "High";
  if (p === 3) return "Medium";
  return "Low";
}

// Map category to verb-first action title
function getVerbTitle(rec: Recommendation): string {
  const verbMap: Record<string, string> = {
    riskDiversification: "Reduce concentration risk",
    downsideResilience: "Strengthen downside protection",
    performanceOptimization: "Optimize portfolio performance",
    costAnalysis: "Cut investment fees",
    taxEfficiency: "Improve tax efficiency",
    riskAdjusted: "Boost risk-adjusted returns",
    planningGaps: "Complete planning checklist",
    lifetimeIncomeSecurity: "Secure retirement income",
    performanceMetrics: "Review performance metrics",
  };
  
  // If the original title already starts with a verb, use it
  const firstWord = rec.title.split(' ')[0]?.toLowerCase() || '';
  const actionVerbs = ['reduce', 'cut', 'improve', 'boost', 'fix', 'add', 'review', 'secure', 'complete', 'optimize', 'strengthen', 'diversify', 'rebalance', 'consider'];
  
  if (actionVerbs.includes(firstWord)) {
    return rec.title;
  }
  
  return verbMap[rec.category] || `Address ${rec.title.toLowerCase()}`;
}

// Map category to "why this matters" text
function getWhyItMatters(rec: Recommendation): string {
  const whyMap: Record<string, string> = {
    riskDiversification: "One bad investment shouldn't derail your plan",
    downsideResilience: "Protect yourself from market surprises",
    performanceOptimization: "Make your money work harder",
    costAnalysis: "Fees eat into your returns over time",
    taxEfficiency: "Keep more of what you earn",
    riskAdjusted: "Get better returns for the risk you take",
    planningGaps: "Small gaps can become big problems",
    lifetimeIncomeSecurity: "Never worry about outliving your money",
    performanceMetrics: "Know how your portfolio is really doing",
  };
  
  // Use impact if available, otherwise use mapped text
  if (rec.impact) {
    return rec.impact;
  }
  
  return whyMap[rec.category] || "This can improve your financial position";
}

export const ActionPlanPanel = forwardRef<ActionPlanPanelRef, ActionPlanPanelProps>(
  function ActionPlanPanel({ actionPlan, onSelectCategory, planningProgress }, ref) {

  useImperativeHandle(ref, () => ({
    expand: () => {},
    toggle: () => {},
  }));

  // Group actions
  const fixFirst = actionPlan.filter(r => r.priority <= 2);
  const optimizeNext = actionPlan.filter(r => r.priority > 2);

  // Determine planning progress status
  const planningComplete = planningProgress ? planningProgress.completed === planningProgress.total : false;
  const planningPercentage = planningProgress
    ? Math.round((planningProgress.completed / planningProgress.total) * 100)
    : 0;

  return (
    <div className="rounded-xl border border-border/50 bg-card p-4 sm:p-5 space-y-4">
      {/* Header with Action Plan title and Planning Progress */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <ListTodo className="h-5 w-5 text-primary shrink-0" />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-base">Action Plan</span>
              {actionPlan.length > 0 && (
                <Badge variant="secondary" className="text-xs px-1.5 py-0">
                  {actionPlan.length} item{actionPlan.length !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {fixFirst.length > 0
                ? `${fixFirst.length} high priority item${fixFirst.length > 1 ? 's' : ''} to address`
                : actionPlan.length > 0
                  ? "Optimization opportunities available"
                  : "No immediate actions needed"
              }
            </p>
          </div>
        </div>

        {/* Planning Progress */}
        {planningProgress && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 border border-border/30">
            <ClipboardCheck className={`h-4 w-4 ${planningComplete ? 'text-status-good' : 'text-muted-foreground'}`} />
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">
                {planningProgress.completed}/{planningProgress.total}
              </span>
              <span className="text-xs text-muted-foreground">planning items</span>
            </div>
            {/* Mini progress bar */}
            <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${planningComplete ? 'bg-status-good' : 'bg-primary'}`}
                style={{ width: `${planningPercentage}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Action items - always visible */}
      {actionPlan.length > 0 && (
        <div className="space-y-4">
          {/* Fix First Section */}
          {fixFirst.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Fix First
              </h4>
              <div className="space-y-1.5">
                {fixFirst.map((rec) => (
                  <ActionItem
                    key={rec.id}
                    recommendation={rec}
                    onSelectCategory={onSelectCategory}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Optimize Next Section */}
          {optimizeNext.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Optimize Next
              </h4>
              <div className="space-y-1.5">
                {optimizeNext.map((rec) => (
                  <ActionItem
                    key={rec.id}
                    recommendation={rec}
                    onSelectCategory={onSelectCategory}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {actionPlan.length === 0 && (
        <div className="text-center py-4">
          <p className="text-sm text-muted-foreground">
            Great job! Your portfolio is in good shape.
          </p>
        </div>
      )}
    </div>
  );
});

interface ActionItemProps {
  recommendation: Recommendation;
  onSelectCategory?: (categoryKey: string) => void;
}

function ActionItem({ recommendation, onSelectCategory }: ActionItemProps) {
  const verbTitle = getVerbTitle(recommendation);
  const whyItMatters = getWhyItMatters(recommendation);

  return (
    <div className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-muted/30 border border-border/30">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Badge variant={priorityVariant(recommendation.priority)} className="text-[10px] px-1.5 py-0 shrink-0">
            {priorityLabel(recommendation.priority)}
          </Badge>
          <span className="text-sm font-medium truncate">{verbTitle}</span>
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5 truncate pl-[calc(theme(spacing.1.5)+theme(spacing.2)+2ch)]">
          {whyItMatters}
        </p>
      </div>
      {recommendation.category && onSelectCategory && (
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0 h-7 px-2 text-xs"
          onClick={(e) => {
            e.stopPropagation();
            onSelectCategory(recommendation.category);
          }}
        >
          View
          <ArrowRight className="h-3 w-3 ml-1" />
        </Button>
      )}
    </div>
  );
}
